import type { Schemas } from '#shopware'
import type { WarrantyLabelData } from '~/composables/useProductWarrantyLabel'

/**
 * Extracts warranty label data from product and manufacturer data
 * Shared logic used by both useProductWarrantyLabel and useCartWarrantyLabels
 */
export function extractWarrantyDataFromProduct(
    productData: Schemas['Product'],
    manufacturer: Schemas['ProductManufacturer']
): WarrantyLabelData | null {
    // Extract custom fields with proper typing
    const manufacturerTranslated = manufacturer?.translated as { name?: string; customFields?: Record<string, unknown> } | undefined
    const manufacturerCustomFields = manufacturerTranslated?.customFields
    if (!manufacturerCustomFields) return null

    const productTranslated = productData.translated as { customFields?: Record<string, unknown> } | undefined
    const productCustomFields = productTranslated?.customFields

    // Check if product explicitly hides the label
    if (productCustomFields?.custom_a4g_warranty_label_product_hide === true) {
        return null
    }

    // Get warranty years - product override or manufacturer default
    const warrantyYearsRaw = productCustomFields?.custom_a4g_warranty_label_product_years
        ?? manufacturerCustomFields?.custom_a4g_warranty_label_years

    // legal requirement of the european union - ensure it's a valid number > 2
    if (typeof warrantyYearsRaw !== 'number' || warrantyYearsRaw <= 2) {
        return null
    }

    const warrantyYears: number = warrantyYearsRaw

    // Get brand from manufacturer name
    const manufacturerTranslatedName = manufacturerTranslated?.name
    const manufacturerName = (manufacturer as { name?: string }).name
    const brand = (typeof manufacturerTranslatedName === 'string' ? manufacturerTranslatedName : null)
        ?? (typeof manufacturerName === 'string' ? manufacturerName : null)

    if (!brand) {
        return null
    }

    // Get model identifier from product number/EAN/manufacturer number
    const modelIdentifier = productData.productNumber
        ?? productData.ean
        ?? productData.manufacturerNumber

    if (!modelIdentifier) {
        return null
    }

    const conditionsLink = 'https://europa.eu/youreurope/business/dealing-with-customers/consumer-contracts-guarantees/consumer-guarantees/index_en.htm'

    return {
        show: true,
        warrantyYears,
        brand,
        modelIdentifier,
        conditionsLink,
        mediaUrl: null,
        useGeneratedSvg: true,
    }
}
